import {
  Button,
  Dialog,
  RoleVisibilitySelect,
  TextInput,
  Toggle,
} from "@vellum/ui";
import type { FeatureFlagSchema } from "@vellum/vellum-api";
import { useFormik } from "formik";
import * as yup from "yup";

import { FlagUserSelect } from "@/components/FlagUserSelect";

export interface FeatureFlagFormValues {
  key: string;
  description: string;
  enabled: boolean;
  everyone: boolean;
  roles: string[];
  user_ids: string[];
}

const validationSchema = yup.object({
  key: yup
    .string()
    .trim()
    .matches(
      // <app>.<feature> — flags gate more than Luna now (e.g. reporting.edit).
      /^[a-z0-9-]+\.[a-z0-9-]+$/,
      "Use the <app>.<feature> convention, e.g. luna.scheduling-v2",
    )
    .required("Key is required"),
  description: yup.string().trim(),
});

interface FeatureFlagFormDialogProps {
  isOpen: boolean;
  /** The flag being edited, or null when creating a new one. */
  flag: FeatureFlagSchema | null;
  onClose: () => void;
  onSubmit: (values: FeatureFlagFormValues) => Promise<void>;
}

export function FeatureFlagFormDialog({
  isOpen,
  flag,
  onClose,
  onSubmit,
}: FeatureFlagFormDialogProps) {
  const formik = useFormik<FeatureFlagFormValues>({
    enableReinitialize: true,
    initialValues: {
      key: flag?.key ?? "",
      description: flag?.description ?? "",
      enabled: flag?.enabled ?? false,
      everyone: flag?.everyone ?? false,
      roles: flag?.roles ?? [],
      user_ids: flag?.user_ids ?? [],
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const errorFor = (field: keyof FeatureFlagFormValues) =>
    formik.touched[field]
      ? (formik.errors[field] as string | undefined)
      : undefined;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={flag ? "Edit feature flag" : "Add feature flag"}
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <TextInput
          id="key"
          name="key"
          label="Key"
          placeholder="luna.scheduling-v2"
          helpertext="<app>.<feature>, e.g. luna.scheduling-v2 or reporting.edit"
          value={formik.values.key}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={errorFor("key") ? "error" : undefined}
          errortext={errorFor("key")}
        />
        <TextInput
          id="description"
          name="description"
          label="Description"
          placeholder="What this flag gates"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <Toggle
          label="Enabled"
          helperText="Master switch. When off, the flag is off for everyone."
          checked={formik.values.enabled}
          onChange={(event) =>
            void formik.setFieldValue("enabled", event.target.checked)
          }
        />
        <Toggle
          label="Everyone"
          helperText="When on (and enabled), the flag is on for all users."
          checked={formik.values.everyone}
          onChange={(event) =>
            void formik.setFieldValue("everyone", event.target.checked)
          }
        />
        <RoleVisibilitySelect
          itemLabel="flag"
          value={formik.values.roles}
          onChange={(roles) => void formik.setFieldValue("roles", roles)}
        />
        <FlagUserSelect
          value={formik.values.user_ids}
          onChange={(userIds) => void formik.setFieldValue("user_ids", userIds)}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={formik.isSubmitting}>
            {flag ? "Save changes" : "Add flag"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
